import { useState } from "react";
import { toast } from "sonner";
import { apiError } from "@/lib/api";
import { Loader2, Send, ShieldCheck } from "lucide-react";

const ReplyItem = ({ r }) => (
    <div
        className={`border-l-2 pl-3 py-2 ${
            r.author === "admin" ? "border-blaze bg-blaze/5" : "border-white/20 bg-white/[0.02]"
        }`}
        data-testid={`ticket-reply-${r.reply_id}`}
    >
        <div className="flex items-center gap-2 font-mono text-[9px] uppercase tracking-[0.2em] mb-1">
            {r.author === "admin" && <ShieldCheck className="w-3 h-3 text-blaze" />}
            <span className={r.author === "admin" ? "text-blaze" : "text-white/50"}>
                {r.author_name}
            </span>
            <span className="text-white/25">{new Date(r.created_at).toLocaleString()}</span>
        </div>
        <p className="font-mono text-xs text-white/70 leading-relaxed whitespace-pre-wrap">
            {r.message}
        </p>
    </div>
);

const RepliesList = ({ replies }) => {
    if (replies.length === 0) return null;
    return (
        <div className="space-y-2 mb-3">
            {replies.map((r) => (
                <ReplyItem key={r.reply_id} r={r} />
            ))}
        </div>
    );
};

const ReplyForm = ({ ticketId, viewerRole, onReply }) => {
    const [text, setText] = useState("");
    const [busy, setBusy] = useState(false);

    const submit = async (e) => {
        e.preventDefault();
        if (!text.trim()) return;
        setBusy(true);
        try {
            await onReply(text.trim());
            setText("");
        } catch (err) {
            toast.error(apiError(err));
        } finally {
            setBusy(false);
        }
    };

    return (
        <form onSubmit={submit} className="flex gap-2">
            <input
                value={text}
                onChange={(e) => setText(e.target.value)}
                maxLength={4000}
                placeholder={viewerRole === "admin" ? "Reply as SPCLIVE Support..." : "Reply to support..."}
                className="flex-1 min-w-0 bg-obsidian border border-white/10 text-white px-3 py-2 font-mono text-xs outline-none focus:border-electric placeholder:text-white/20 transition-colors"
                data-testid={`ticket-reply-input-${ticketId}`}
            />
            <button
                type="submit"
                disabled={busy || !text.trim()}
                className="px-4 border border-white/20 hover:border-blaze hover:text-blaze disabled:opacity-30 disabled:cursor-not-allowed text-white/70 transition-colors"
                data-testid={`ticket-reply-send-${ticketId}`}
            >
                {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Send className="w-3.5 h-3.5" />}
            </button>
        </form>
    );
};

export const TicketThread = ({ ticket, onReply, viewerRole }) => (
    <div className="mt-3" data-testid={`ticket-thread-${ticket.ticket_id}`}>
        <RepliesList replies={ticket.replies || []} />
        <ReplyForm ticketId={ticket.ticket_id} viewerRole={viewerRole} onReply={onReply} />
    </div>
);
